<template>
  <article class="wx-article" :lang="isEnglish ? 'en' : 'zh-CN'">
    <div class="wx-inner">
      <h1 class="wx-title">{{ article.title }}</h1>
      <template v-for="(block, index) in article.blocks" :key="index">
        <div v-if="block.type === 'image'" class="wx-media">
          <img :src="block.src" :alt="article.title" loading="lazy" />
        </div>
        <h2 v-else-if="block.type === 'h2'" class="wx-h2">{{ block.text }}</h2>
        <p v-else-if="block.type === 'p'" class="wx-p">
          <span v-for="(seg, si) in block.segs" :key="si" :class="seg.c">
            <a v-if="seg.u" :href="seg.u" target="_blank" rel="noopener noreferrer">{{ seg.t }}</a>
            <template v-else>{{ seg.t }}</template>
          </span>
        </p>
        <p v-else-if="block.type === 'meta'" class="wx-meta">
          <span v-for="(seg, si) in block.segs" :key="si">
            <a v-if="seg.u" :href="seg.u" target="_blank" rel="noopener noreferrer">{{ seg.t }}</a>
            <template v-else>{{ seg.t }}</template>
          </span>
        </p>
      </template>
    </div>
  </article>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { locale } = useI18n();
const isEnglish = computed(() => locale.value === "en");

// 图片放在媒体库，按顺序编号（P2.jpg 封面 / P3-P4 正文配图）
const IMG = "https://www.healthcode.com/media/20261103";

const img = (name) => ({ type: "image", src: `${IMG}/${name}` });
const h2 = (text) => ({ type: "h2", text });
const p = (...segs) => ({
  type: "p",
  segs: segs.map((s) => (typeof s === "string" ? { t: s, c: "" } : s)),
});
const b = (t) => ({ t, c: "b" });
const hl = (t) => ({ t, c: "hl" });
const meta = (...segs) => ({
  type: "meta",
  segs: segs.map((s) => (typeof s === "string" ? { t: s, c: "" } : s)),
});
const link = (t, u) => ({ t, u });

const siteLinks = (phone, mail, addr) => [
  meta("网站：", link("www.healthcode.com", "https://www.healthcode.com"), " / ", link("www.ourproteinfactory.com", "https://www.ourproteinfactory.com")),
  meta(phone, "+86-400-770-5500 / +1-800-955-3390"),
  meta(mail, "customer@ourproteinfactory.com"),
  meta(addr, "上海市浦东新区"),
];

const content = {
  zh: {
    title: "D2Pi-AutoLab，筛选效率提升11x，成本大幅降低99%！",
    blocks: [
      img("P2.jpg"),
      h2("无细胞体外合成自动化实验室创新产品系列"),
      p(
        hl("D2Pi-AutoLab 系统"),
        "，通量灵活，适用每天",
        hl("1-10000"),
        " 种样品的实验室通量需求，",
        b("从单一样品的核酸提取、细胞培养、基因组鉴定，到高通量DNA/RNA制备、超螺旋质粒纯化、核酸回收、DNA/RNA浓缩、内毒素去除、体外蛋白质合成、纯化、活性和亲和力鉴定"),
        "等，均能实现自动化、高标准，用途广泛，加速推动全球生命科学研究进展。"
      ),
      h2("D2Pi-AutoLab 效能突破"),
      p(
        hl("D2Pi"),
        " 将传统新药研发周期在早期阶段压缩 ",
        hl("50% "),
        "以上。这不是渐进式的改进，而是指数级的效率跨越。",
        b("通过极致的算力支持，将曾经昂贵的生物实验转化为可规模化的计算任务。"),
        b("筛选效率提升"),
        hl("11x"),
        b("，"),
        " 成本大幅降低",
        hl("99%"),
        "。"
      ),
      img("P3.png"),
      h2("以算力驱动生物实验规模化，为生命科学与新药研发效率带来指数级革新"),
      p("依托强大算力，把高成本生物实验转为可规模化运行的标准化任务，以全流程自动化、高标准的实验能力，持续赋能生命科学研究，加速创新药物研发进程。"),
      img("P4.png"),
      h2("结语"),
      p(
        b("康码生物是"),
        hl("无细胞蛋白质合成产业化全球龙头企业"),
        b("，依托累计全球"),
        hl("700余专利件"),
        b("，"),
        hl("1700余件知识产权"),
        b("，康码开发了一系列无细胞蛋白质合成自动化智能方案，"),
        b("D2Pi-AutoLab"),
        b("，实现"),
        b("毫升级以上合成体积，毫克级以上合成得率"),
        b("，满足下游功能鉴定，活性分析，亲和力测试，结构解析，代谢鉴定等量化需求，为全球AI蛋白质智造，提升物理基础“加速器”！")
      ),
      p(
        "更多信息，请访问 ",
        link("www.healthcode.com", "https://www.healthcode.com"),
        " / ",
        link("www.ourproteinfactory.com", "https://www.ourproteinfactory.com"),
        "。"
      ),
      ...siteLinks("电话：", "邮箱：", "地址："),
    ],
  },
  en: {
    title: "D2Pi-AutoLab: Screening Efficiency Up 11x, Costs Down 99%!",
    blocks: [
      img("P2.jpg"),
      h2("Innovative Product Series for Automated Cell-Free In Vitro Synthesis Laboratories"),
      p(
        hl("The D2Pi-AutoLab system"),
        " offers flexible throughput for laboratories requiring ",
        hl("1–10,000"),
        " samples per day. ",
        b("From nucleic acid extraction, cell culture and genome identification for a single sample, to high-throughput DNA/RNA preparation, supercoiled plasmid purification, nucleic acid recovery, DNA/RNA concentration, endotoxin removal, in vitro protein synthesis, purification, activity and affinity characterization"),
        " — all can be performed automatically to high standards, with broad applications that accelerate global life science research."
      ),
      h2("D2Pi-AutoLab Performance Breakthrough"),
      p(
        hl("D2Pi"),
        " compresses the early phase of the traditional new drug R&D cycle by more than ",
        hl("50%"),
        ". This is not an incremental improvement but an exponential leap in efficiency. ",
        b("With extreme computing power, once-expensive biological experiments are transformed into scalable computational tasks."),
        b(" Screening efficiency is up "),
        hl("11x"),
        b(", while "),
        "costs drop by ",
        hl("99%"),
        "."
      ),
      img("P3.png"),
      h2("Driving the Scale-up of Biological Experiments with Computing Power — Exponential Innovation for Life Science and New Drug R&D Efficiency"),
      p("Leveraging powerful computing power, costly biological experiments are converted into standardized tasks that can run at scale. With fully automated, high-standard experimental capability, we continuously empower life science research and accelerate innovative drug development."),
      img("P4.png"),
      h2("Conclusion"),
      p(
        b("Healthcode is the "),
        hl("global leader in the industrialization of cell-free protein synthesis"),
        b(", with "),
        hl("more than 700 patents"),
        b(" and "),
        hl("over 1,700 intellectual property items"),
        b(" worldwide. Healthcode has developed a full series of automated, intelligent cell-free protein synthesis solutions — "),
        b("D2Pi-AutoLab"),
        b(", achieving "),
        b("synthesis volumes at milliliter level and above, with milligram-level or higher yields"),
        b(". It meets quantitative downstream needs such as functional characterization, activity analysis, affinity testing, structural analysis and metabolic identification — a physical “accelerator” for global AI protein manufacturing!")
      ),
      p(
        "For more information, please visit ",
        link("www.healthcode.com", "https://www.healthcode.com"),
        " / ",
        link("www.ourproteinfactory.com", "https://www.ourproteinfactory.com"),
        "."
      ),
      meta("Website: ", link("www.healthcode.com", "https://www.healthcode.com"), " / ", link("www.ourproteinfactory.com", "https://www.ourproteinfactory.com")),
      meta("Tel: ", "+86-400-770-5500 / +1-800-955-3390"),
      meta("Email: ", "customer@ourproteinfactory.com"),
      meta("Address: ", "Pudong New Area, Shanghai, China"),
    ],
  },
};

const article = computed(() => (isEnglish.value ? content.en : content.zh));
</script>

<style scoped>
.wx-article {
  width: 100%;
  min-height: 100vh;
  padding: 8rem 0 4rem;
  background: #fff;
}

.wx-inner {
  max-width: 677px;
  margin: 0 auto;
  padding: 0 16px;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
  font-size: 17px;
  line-height: 1.75;
  letter-spacing: 0.034em;
  color: #333;
  text-align: justify;
  overflow-wrap: break-word;
}

.wx-title {
  margin: 0 0 24px;
  color: #222;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.4;
  text-align: left;
}

.wx-h2 {
  margin: 2.5em 8px 1.2em;
  padding: 0 0 6px;
  border-bottom: 2px solid rgb(15, 76, 129);
  color: rgb(17, 17, 17);
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.03em;
  line-height: 1.4;
  text-align: left;
}

.wx-p {
  margin: 0 0 16px;
}

.wx-media {
  margin: 0 0 16px;
  text-align: center;
}

.wx-media img {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
}

.wx-meta {
  margin: 0 0 6px;
  color: #666;
  font-size: 15px;
  line-height: 1.7;
  text-align: left;
}

.b {
  font-weight: 700;
}

.hl {
  color: rgb(0, 128, 255);
  font-weight: 700;
}

.wx-inner a {
  color: inherit;
  text-decoration: inherit;
}

.wx-inner a:hover {
  color: rgb(0, 128, 255);
}

@media (max-width: 640px) {
  .wx-article {
    padding: 6rem 0 3rem;
  }

  .wx-inner {
    font-size: 16px;
    line-height: 1.7;
  }

  .wx-title {
    font-size: 19px;
  }

  .wx-h2 {
    font-size: 18px;
    margin: 1.8em 0 0.9em;
  }
}
</style>
